import React, { useState, useEffect } from 'react';
import { 
  ChevronRight, 
  ChevronDown, 
  ShieldCheck, 
  Check, 
  ExternalLink, 
  Sliders, 
  Lock, 
  BarChart3, 
  Sparkles, 
  Target,
  X
} from 'lucide-react';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
  marketing: boolean;
  consentedAt: string;
}

const STORAGE_KEY = 'giffarine_cookie_consent_v1';

interface CookieConsentBannerProps {
  isOpen?: boolean;
  onOpenPolicyModal?: (tab?: 'privacy' | 'terms' | 'shipping' | 'pdpa' | 'cookie') => void;
  onClose?: () => void;
}

export const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({
  isOpen: forcedIsOpen,
  onOpenPolicyModal,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: true,
    functional: true,
    marketing: true,
  });

  useEffect(() => {
    if (forcedIsOpen !== undefined) {
      setIsVisible(forcedIsOpen);
      if (forcedIsOpen) {
        // Load saved preferences if available
        try {
          const saved = localStorage.getItem(STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved);
            setPreferences({
              necessary: true,
              analytics: !!parsed.analytics,
              functional: !!parsed.functional,
              marketing: !!parsed.marketing,
            });
          }
        } catch (e) {
          console.error(e);
        }
      }
      return;
    }

    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (!saved) {
        // Show after a brief delay for optimal user experience
        const timer = setTimeout(() => {
          setIsVisible(true);
        }, 800);
        return () => clearTimeout(timer);
      }
    } catch (e) {
      setIsVisible(true);
    }
  }, [forcedIsOpen]);

  const saveConsent = (prefs: { necessary: boolean; analytics: boolean; functional: boolean; marketing: boolean }) => {
    const payload: CookiePreferences = {
      ...prefs,
      necessary: true,
      consentedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {
      console.error(e);
    }
    setIsVisible(false);
    if (onClose) onClose();
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      functional: true,
      marketing: true,
    });
  };

  const handleAcceptNecessaryOnly = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      functional: false,
      marketing: false,
    });
  };

  const handleSaveCustomPreferences = () => {
    saveConsent(preferences);
  };

  if (!isVisible) return null;

  return (
    <div 
      id="cookie-consent-wrapper"
      className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-6 flex justify-center pointer-events-none transition-all duration-300 animate-in fade-in slide-in-from-bottom-6"
    >
      <div 
        id="cookie-consent-banner"
        className="pointer-events-auto w-full max-w-4xl bg-white rounded-2xl sm:rounded-3xl shadow-[0_-8px_30px_rgba(0,0,0,0.12)] border border-slate-200/90 p-5 sm:p-8 text-slate-800 flex flex-col gap-4 max-h-[90vh] overflow-y-auto"
      >
        {/* Header Title */}
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
            การตั้งค่าคุกกี้
          </h2>
          {forcedIsOpen && (
            <button
              onClick={() => {
                setIsVisible(false);
                if (onClose) onClose();
              }}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
              aria-label="ปิด"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Informational Text matching the exact wording and tone */}
        <p className="text-xs sm:text-sm text-slate-700 leading-relaxed font-normal">
          เว็บไซต์มีการใช้งานคุกกี้เพื่อมอบประสบการณ์การใช้งานเว็บไซต์ที่ดียิ่งขึ้นให้แก่คุณ รวมถึง เสนอสิทธิประโยชน์ที่ตรงใจ คุณสามารถเลือกคลิก{' '}
          <strong className="font-bold text-slate-900">“ยอมรับคุกกี้ทั้งหมด”</strong>{' '}
          เพื่อรับมอบประสบการณ์ที่ดีและสิทธิประโยชน์ดังกล่าว หรือคลิก{' '}
          <strong className="font-bold text-slate-900">“การตั้งค่าคุกกี้เพิ่มเติม”</strong>{' '}
          เพื่อเลือกตั้งค่าคุกกี้แต่ละประเภท ศึกษารายละเอียดเพิ่มเติมได้ที่{' '}
          <button
            type="button"
            onClick={() => {
              setIsVisible(false);
              if (onClose) onClose();
              window.history.pushState({}, '', '/cookie-policy');
              window.dispatchEvent(new Event('popstate'));
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="text-[#E60067] hover:text-[#BE185D] font-bold inline-flex items-center gap-1 hover:underline cursor-pointer align-baseline"
          >
            <span>นโยบายการใช้คุกกี้</span>
            <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-[#E60067] text-white text-[9px] font-bold leading-none">
              +
            </span>
          </button>
        </p>

        {/* Divider */}
        <div className="border-t border-slate-200"></div>

        {/* Collapsible Accordion: "การตั้งค่าคุกกี้เพิ่มเติม" */}
        <div>
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full py-2 flex items-center justify-between text-left group cursor-pointer"
          >
            <span className="text-sm sm:text-base font-semibold text-slate-800 group-hover:text-[#DB2777] transition-colors">
              การตั้งค่าคุกกี้เพิ่มเติม
            </span>
            <div className="text-[#DB2777] group-hover:translate-x-0.5 transition-transform">
              {isExpanded ? (
                <ChevronDown className="w-5 h-5 text-[#DB2777]" />
              ) : (
                <ChevronRight className="w-5 h-5 text-[#DB2777]" />
              )}
            </div>
          </button>

          {/* Expanded Cookie Categories Drawer */}
          {isExpanded && (
            <div className="mt-3 pt-3 border-t border-slate-100 space-y-4 animate-in fade-in duration-200">
              {/* Category 1: Necessary */}
              <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 pr-2">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-emerald-600 shrink-0" />
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                      คุกกี้ที่จำเป็นอย่างยิ่ง (Strictly Necessary Cookies)
                    </h4>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-normal">
                    คุกกี้ที่มีความจำเป็นสำหรับการทำงานขั้นพื้นฐานของเว็บไซต์ เช่น การเลือกสินค้าลงตะกร้า การคำนวณคะแนน PV และระบบความปลอดภัยในการเข้าสู่ระบบ คุกกี้ประเภทนี้ไม่สามารถปิดได้
                  </p>
                </div>
                <div className="shrink-0 flex items-center">
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-full border border-emerald-200 inline-flex items-center gap-1">
                    <Check className="w-3.5 h-3.5" />
                    <span>เปิดตลอดเวลา</span>
                  </span>
                </div>
              </div>

              {/* Category 2: Analytics */}
              <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 pr-2">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-blue-600 shrink-0" />
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                      คุกกี้เพื่อการวิเคราะห์และวัดผล (Analytics Cookies)
                    </h4>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-normal">
                    ช่วยให้เราเข้าใจการเข้าชมและการใช้งานเว็บไซต์ เพื่อปรับปรุงความรวดเร็วและพัฒนาประสบการณ์การซื้อสินค้าของสมาชิก
                  </p>
                </div>
                <div className="shrink-0 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) =>
                        setPreferences({ ...preferences, analytics: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#DB2777]"></div>
                  </label>
                </div>
              </div>

              {/* Category 3: Functional */}
              <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 pr-2">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                      คุกกี้เพื่อการทำงานของเว็บไซต์ (Functional Cookies)
                    </h4>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-normal">
                    ช่วยจดจำการตั้งค่า เช่น สินค้าที่ดูบ่อย การบันทึกรายการโปรด และการจัดส่งที่คุ้นเคยเพื่ออำนวยความสะดวกสูงสุด
                  </p>
                </div>
                <div className="shrink-0 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.functional}
                      onChange={(e) =>
                        setPreferences({ ...preferences, functional: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#DB2777]"></div>
                  </label>
                </div>
              </div>

              {/* Category 4: Marketing */}
              <div className="p-3.5 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1 pr-2">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-rose-500 shrink-0" />
                    <h4 className="text-xs sm:text-sm font-bold text-slate-900">
                      คุกกี้เพื่อการตลาดและสิทธิประโยชน์ (Marketing Cookies)
                    </h4>
                  </div>
                  <p className="text-[11px] sm:text-xs text-slate-600 leading-normal">
                    นำเสนอแคมเปญโปรโมชั่น ส่วนลดพิเศษ และสิทธิประโยชน์ของกิฟฟารีนที่ตรงใจและเหมาะสมกับความต้องการของคุณ
                  </p>
                </div>
                <div className="shrink-0 flex items-center">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) =>
                        setPreferences({ ...preferences, marketing: e.target.checked })
                      }
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#DB2777]"></div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons: Centered "ยอมรับคุกกี้ทั้งหมด" in signature pink */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
          {isExpanded ? (
            <div className="w-full flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2.5">
              <button
                type="button"
                onClick={handleAcceptNecessaryOnly}
                className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-semibold transition-all cursor-pointer text-center"
              >
                ปฏิเสธคุกกี้ที่ไม่จำเป็น
              </button>
              <button
                type="button"
                onClick={handleSaveCustomPreferences}
                className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-xs sm:text-sm font-bold transition-all cursor-pointer text-center"
              >
                บันทึกการตั้งค่า
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-7 py-2.5 rounded-xl bg-gradient-to-r from-[#FF6B97] via-[#F43F85] to-[#DB2777] hover:from-pink-600 hover:to-pink-700 text-white text-xs sm:text-sm font-bold shadow-md shadow-pink-500/25 transition-all active:scale-[0.98] cursor-pointer text-center"
              >
                ยอมรับคุกกี้ทั้งหมด
              </button>
            </div>
          ) : (
            <div className="w-full flex items-center justify-center">
              <button
                type="button"
                id="btn-accept-all-cookies"
                onClick={handleAcceptAll}
                className="w-full sm:w-auto min-w-[220px] px-9 py-3 rounded-xl bg-gradient-to-r from-[#FF6B97] via-[#F43F85] to-[#DB2777] hover:from-pink-600 hover:to-pink-700 text-white text-sm sm:text-base font-bold shadow-md shadow-pink-500/25 transition-all active:scale-[0.98] cursor-pointer text-center"
              >
                ยอมรับคุกกี้ทั้งหมด
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
